import type { IconType } from '@ais-ui/ui-core';
import { PiBoat, PiClockCountdown, PiMapPin, PiQuestion } from 'react-icons/pi';

/**
 * Stable, UI-facing identity for a backend reason. The backend only ships the
 * human-readable string, so we classify by matching it and fall back to
 * `unknown` for anything unrecognised.
 */
export type NoPredictionReasonKind =
  | 'in-port'
  | 'too-old'
  | 'first-trip'
  | 'no-data'
  | 'unknown';

export interface NoPredictionReason {
  kind: NoPredictionReasonKind;
  /** Short display title, localized by the card. */
  titleKey: string;
  /** Explanatory sentence, localized by the card. */
  bodyKey: string;
  /** Footer hint describing when prediction resumes, localized by the card. */
  footerKey: string;
  icon: IconType;
}

const REASONS: Record<
  Exclude<NoPredictionReasonKind, 'unknown'>,
  NoPredictionReason
> = {
  'in-port': {
    kind: 'in-port',
    titleKey: 'shipDetailsNextPortReasonInPortTitle',
    bodyKey: 'shipDetailsNextPortReasonInPortBody',
    footerKey: 'shipDetailsNextPortReasonInPortFooter',
    icon: PiMapPin,
  },
  'too-old': {
    kind: 'too-old',
    titleKey: 'shipDetailsNextPortReasonTooOldTitle',
    bodyKey: 'shipDetailsNextPortReasonTooOldBody',
    footerKey: 'shipDetailsNextPortReasonTooOldFooter',
    icon: PiClockCountdown,
  },
  'first-trip': {
    kind: 'first-trip',
    titleKey: 'shipDetailsNextPortReasonFirstTripTitle',
    bodyKey: 'shipDetailsNextPortReasonFirstTripBody',
    footerKey: 'shipDetailsNextPortReasonFirstTripFooter',
    icon: PiBoat,
  },
  'no-data': {
    kind: 'no-data',
    titleKey: 'shipDetailsNextPortReasonNoDataTitle',
    bodyKey: 'shipDetailsNextPortReasonNoDataBody',
    footerKey: 'shipDetailsNextPortReasonNoDataFooter',
    icon: PiQuestion,
  },
};

/**
 * Maps the backend's reason string to a known reason kind. The shiptracker
 * webservice emits a fixed set of strings (see `get_reason()` in
 * `impl/shiptracker/webservice.cpp` in ais-main); we match on the meaningful
 * substring so minor backend wording changes don't drop us into the generic
 * fallback. `"No predictions because there are no data available"` maps to
 * `no-data`, while `"No predictions were made"` / `"No predictions for unknown
 * reasons"` intentionally fall through to the generic card.
 */
export const classifyNoPredictionReason = (
  reason: string,
): NoPredictionReasonKind => {
  const normalized = reason.trim().toLowerCase();
  if (normalized.includes('currently in port')) return 'in-port';
  if (normalized.includes('too old information')) return 'too-old';
  if (normalized.includes('first trip')) return 'first-trip';
  if (normalized.includes('no data available')) return 'no-data';
  return 'unknown';
};

/**
 * Resolves the display model for a backend reason string. Unknown reasons fall
 * back to a generic card whose body shows the raw backend text, so nothing the
 * backend sends is ever hidden.
 */
export const resolveNoPredictionReason = (
  reason: string,
): NoPredictionReason => {
  const kind = classifyNoPredictionReason(reason);
  if (kind === 'unknown') {
    return {
      kind,
      titleKey: 'shipDetailsNextPortReasonNoDataTitle',
      bodyKey: 'shipDetailsNextPortReasonNoDataBody',
      footerKey: 'shipDetailsNextPortReasonNoDataFooter',
      icon: PiQuestion,
    };
  }
  return REASONS[kind];
};
